import { createContext, useContext } from 'react';

/**
 * useDiagnosisFormField — public SDK hook that lets specialty
 * extensions (passed via `opts.extras` to DiagnosisWidget) read and
 * write fields on the widget's form state.
 *
 * Why this exists
 * ---------------
 * Dental needs to contribute common-diagnosis quick chips (set
 * `content` + `severity` in one tap) and a surface picker M/D/B/L/O
 * (write to `surfaces`). The widget body owns the form state; an
 * extension passed as a static `ReactNode` can't write into it without
 * a side channel. This hook is that side channel — a tiny React
 * context that DiagnosisWidget wraps around its body via
 * `<DiagnosisFormProvider>`. Extensions consume the hook and call the
 * setter; the widget re-renders with the new value.
 *
 * Public surface
 * --------------
 *   const [value, setValue] = useDiagnosisFormField('content');
 *
 * Supported keys (MVP): `'content' | 'severity' | 'surfaces'`. Adding
 * a new key is a widget-library v1 SDK extension (add to the provider
 * value, document the new key here).
 *
 * Outside-of-widget behaviour
 * ---------------------------
 * If called from a component NOT rendered inside DiagnosisWidget (no
 * provider in scope), the hook returns `[undefined, noopSetter]` and
 * emits a dev-mode console warning. Production stays silent — never
 * throws.
 */

const DiagnosisFormContext = createContext(null);

export const DiagnosisFormProvider = DiagnosisFormContext.Provider;

const NOOP_SETTER = () => {};

export function useDiagnosisFormField(key) {
  const ctx = useContext(DiagnosisFormContext);
  if (!ctx) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `[widgets] useDiagnosisFormField('${key}') called outside DiagnosisWidget; `
        + 'extension must be passed via opts.extras to DiagnosisWidget for state writes to work.',
      );
    }
    return [undefined, NOOP_SETTER];
  }
  const value = ctx.values?.[key];
  const setter = (next) => ctx.set(key, next);
  return [value, setter];
}

export default useDiagnosisFormField;
